import {
  ArrowForwardIos,
  ArrowBackIos,
  PictureAsPdfOutlined,
} from "@mui/icons-material";
import {
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  DialogActions,
  DialogContent,
  TextField,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, DataDialog, DataTooltip } from "@app/components/atoms";
import { useTranslation } from "react-i18next";
import {
  IconBox,
  IconDeleteGray,
  // IconEdit,
  IconInputError,
} from "@app/components/icons";
import {
  addMonths,
  format,
  formatISO,
  getDaysInMonth,
  getMonth,
  getYear,
} from "date-fns";
import {
  getInspectionFormsWeb,
  getInspections,
  getInspectionPDF,
  getMonthlyInspectionPDF,
  getUser,
  getUserPermissions,
  apiGetReportMachineNameInfo,
  apiGetMonthlyInspectionInfo,
  getCustomInspectionFormWeb,
  apiGetInspectionDetail,
} from "@app/api";
import { useParams } from "react-router-dom";
import { CustomInspectionFormsType } from "@app/types";
import {
  DATE_TIME_FORMAT_1,
  DATE_TIME_FORMAT_YEAR_AND_MONTH,
  DATE_TIME_FORMAT_YEAR_AND_MONTH_1,
  ItemInspectionEnum,
  MIN_DATE_CREATE_INSPECTION,
} from "@app/constants";
import { TableInspectionFormTab } from "@app/pages/Features";
import { useSelector } from "react-redux";
import { RootState, setShowAlert, store } from "@app/store";
import {
  formatTime,
  isMobileDevice,
  scrollToTop,
  resetIsContinuousLoading,
  isSafari,
  isMobile,
  getCurrentUtc,
  REGEX,
  clearMachineReportsPreferences,
} from "@app/helpers";
import { DatePickerInput } from "@app/pages/Features/MachineDetail/Components/InspectionHistory/DatePickerInputUpdate";
import { AxiosError } from "axios";
import { useOutletContext } from "react-router-dom";
import React from "react";
import { CreateInspections } from "@app/pages/Features/MachineDetail/Components/Report/CreateReports";
import type {
  InspectionFormDetailResponse,
  LatestInspectionData,
} from "@app/pages/Features/MachineDetail/Components/Report/CreateReports/Inspection/createInspectionForm.types";

type CreateInspectionPrefill = {
  mode?: "create" | "update";
  inspectionId?: string;
  customInspectionFormId: string;
  inspectionAt: string;
  inspectionDetail: InspectionFormDetailResponse;
  latestInspection?: LatestInspectionData | null;
  existingInspection?: LatestInspectionData | null;
};

function parseWebInspectionFormResponse(data: Record<string, unknown>): {
  inspectionDetail: InspectionFormDetailResponse;
  latestInspection: LatestInspectionData | null;
} {
  const latestInspection = (data.latestInspection ??
    data.previousInspection ??
    null) as LatestInspectionData | null;

  const detail = data as InspectionFormDetailResponse;
  const inspectionItems = Array.isArray(detail.inspectionItems)
    ? [...detail.inspectionItems].sort(
        (a, b) => (a.position ?? 0) - (b.position ?? 0),
      )
    : [];

  return {
    inspectionDetail: {
      ...detail,
      inspectionItems,
    },
    latestInspection: latestInspection
      ? {
          ...latestInspection,
          inspectionItems: Array.isArray(latestInspection.inspectionItems)
            ? [...latestInspection.inspectionItems].sort(
                (a, b) => (a.position ?? 0) - (b.position ?? 0),
              )
            : latestInspection.inspectionItems,
        }
      : null,
  };
}

function sortCustomInspectionItemsByPosition<T extends { position?: number }>(
  items: T[] | Record<string, T> | null | undefined,
): T[] {
  const list = Array.isArray(items)
    ? items
    : items
    ? (Object.values(items) as T[])
    : [];
  return [...list].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
}

export function InspectionHistory() {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [openCreateInspection, setOpenCreateInspection] = useState(false);
  const [createInspectionPrefill, setCreateInspectionPrefill] =
    useState<CreateInspectionPrefill | null>(null);
  const [isSubmittingAddInspection, setIsSubmittingAddInspection] =
    useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState(() => {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const period = hours >= 12 ? "PM" : "AM";
    const formattedHours = hours % 12 || 12;
    const formattedMinutes = minutes.toString().padStart(2, "0");
    const formattedTime = `${formattedHours}:${formattedMinutes} ${period}`;
    return formattedTime;
  });
  const [InspectionPDFdialogOpen, setInspectionPDFdialogOpen] = useState(false);
  const [pdfHeaderRemarks, setPdfHeaderRemarks] = useState<string>("");
  const [pdfFooterRemarks, setPdfFooterRemarks] = useState<string>("");
  const [pdfHeaderRemarksError, setPdfHeaderRemarksError] = useState(false);
  const [pdfFooterRemarksError, setPdfFooterRemarksError] = useState(false);
  const [refreshKey] = useState(0);
  const [isButtonVisiblePer, setIsButtonVisiblePer] = useState(false);
  const [userTimeFormat1, setUserTimeFormat1] = useState<string>("yyyy/MM");
  const [userTimeFormat, setUserTimeFormat] = useState<string>("yyyy/MM/dd");
  const { i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const [, setSelectedItem] = useState("");
  const { t } = useTranslation();
  const { groupId, machineId } = useParams();
  const [dateInspection, setDateInspection] = useState(new Date());
  const [dbTemplateList, setDbTemplateList] =
    useState<CustomInspectionFormsType[]>();
  const [inspectionFormName, setInspectionFormName] = useState<string>();
  const [displayData, setDisplayData] = useState([]);

  const isButtonVisible = useMemo(() => {
    if (!inspectionFormName || !dbTemplateList?.length) return false;
    const form = dbTemplateList.find(
      (f) => f.customInspectionFormId === inspectionFormName,
    );
    return form?.currentStatus === "PUBLISHED";
  }, [dbTemplateList, inspectionFormName]);

  const { isRequestedMachineInfo } = useSelector(
    (state: RootState) => state.inspection,
  );
  const [isRequestInspections, setIsRequestInspections] = useState(false);
  const [isInit, setIsInit] = useState(true);
  const [inspectionFormNameStatus, setInspectionFormNameStatus] =
    useState(false);
  const handleChange = (event: SelectChangeEvent) => {
    setInspectionFormName(event.target.value);
  };
  const handleChangeMonth = (value: number) => {
    setDateInspection(addMonths(dateInspection, value));
  };
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteRow, setShowDeleteRow] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [, setClickedInspectionIds] = useState<string[]>([]);
  const { refreshMachineData } = useOutletContext<{
    refreshMachineData: () => void;
  }>();

  const handleOpenPopup = () => {
    const now = new Date();
    const formattedDate = now.toLocaleDateString("en-CA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    const formattedTime = now.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    setSelectedDate(formattedDate);
    setSelectedTime(formattedTime);
    setIsPopupOpen(true);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
    setSelectedDate("");
    setSelectedTime("");
    setSelectedItem("");
  };

  const handleCloseCreateInspection = () => {
    setOpenCreateInspection(false);
    setCreateInspectionPrefill(null);
  };

  const handleEditInspection = async (inspectionId: string) => {
    if (!groupId || !machineId || !inspectionId) return;

    try {
      const response = await apiGetInspectionDetail(
        groupId,
        machineId,
        inspectionId,
      );
      const data = response?.data?.data as LatestInspectionData | undefined;
      if (!data?.inspectionFormId) {
        store.dispatch(
          setShowAlert(t("Anunexpectederroroccurredwhileprocessingthefile")),
        );
        return;
      }

      setCreateInspectionPrefill({
        mode: "update",
        inspectionId,
        customInspectionFormId: data.inspectionFormId,
        inspectionAt: data.inspectionAt || "",
        inspectionDetail: {
          inspectionFormId: data.inspectionFormId,
          name: data.name || "",
          type: data.type,
          inspectionItems: sortCustomInspectionItemsByPosition(
            data.inspectionItems as any,
          ),
        },
        existingInspection: {
          ...data,
          inspectionItems: sortCustomInspectionItemsByPosition(
            data.inspectionItems as any,
          ),
        },
      });
      setOpenCreateInspection(true);
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        store.dispatch(
          setShowAlert(
            error.response?.data?.error?.message ||
              error.response?.data?.meta?.errorMessage ||
              t("Anunexpectederroroccurredwhileprocessingthefile"),
          ),
        );
      } else {
        store.dispatch(
          setShowAlert(t("Anunexpectederroroccurredwhileprocessingthefile")),
        );
      }
    }
  };

  const handleChangeStartDate = (newValue: Date) => {
    const formattedDate = new Intl.DateTimeFormat("en-CA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(newValue);
    setSelectedDate(formattedDate);
  };

  const getFormattedDateTime = () => {
    if (!selectedDate || !selectedTime) return "";
    const year = getYear(dateInspection);
    const month = getMonth(dateInspection);
    const day = new Date(selectedDate).getDate();
    const combinedDate = new Date(year, month, day);
    const combinedDateTime = new Date(
      `${formatISO(combinedDate, { representation: "date" })}T${selectedTime}`,
    );
    if (isNaN(combinedDateTime.getTime())) return "";
    return combinedDateTime.toISOString();
  };

  const handleEditForm = () => {
    setIsEditing(true);
    setShowDeleteRow(true);
  };

  const handleSaveForm = () => {
    setIsEditing(false);
    setShowDeleteRow(false);
    refreshMachineData();
    refreshInspectionData();
  };

  const handleInputPdfHeaderChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value;
    const isInvalid =
      !REGEX.commonNameFieldCharacterMuiltiLineRegex.test(value) ||
      !REGEX.emojiRegex.test(value);

    setPdfHeaderRemarks(value);
    setPdfHeaderRemarksError(isInvalid);
  };

  const handleInputPdfFooterChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value;
    const isInvalid =
      !REGEX.commonNameFieldCharacterMuiltiLineRegex.test(value) ||
      !REGEX.emojiRegex.test(value);

    setPdfFooterRemarks(value);
    setPdfFooterRemarksError(isInvalid);
  };

  const handleOutputMonthlyInspectionPdf = async () => {
    if (groupId && machineId && inspectionFormName) {
      const res = await apiGetMonthlyInspectionInfo(
        groupId,
        machineId,
        inspectionFormName,
      );
      const machineName = res?.data?.data?.machineName;
      const customInspectionFormName = res?.data?.data?.formName;
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      getMonthlyInspectionPDF(
        groupId,
        machineId,
        inspectionFormName,
        getCurrentUtc(),
        timeZone,
        pdfHeaderRemarks,
        pdfFooterRemarks,
        dateInspection,
        dateInspection,
        userTimeFormat,
        customInspectionFormName,
        machineName,
      )
        .then(() => {
          store.dispatch(setShowAlert(t("PDFDownloadedSuccessfully")));
          setInspectionPDFdialogOpen(false);
          setPdfHeaderRemarks("");
          setPdfFooterRemarks("");
        })
        .catch((error) => {
          console.error("PDF download failed:", error);
        });
    }
  };

  const handleOutputIDPdfDirect = async (
    inspectionId: string,
    headerRemarks: string,
    footerRemarks: string,
  ) => {
    if (!groupId || !machineId || !inspectionFormName) return;

    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const res = await apiGetReportMachineNameInfo(inspectionId);
    const machineName = res?.data?.data?.customerMachineName;
    const timeFormat = res?.data?.data?.timeFormat;
    const inspectionAt = res?.data?.data?.inspectionAt;
    const customInspectionFormName = res?.data?.data?.customInspectionFormName;
    const reportName = t("labelInspection");

    getInspectionPDF(
      groupId,
      machineId,
      inspectionId,
      userTimeZone,
      headerRemarks,
      footerRemarks,
      machineName,
      timeFormat,
      reportName,
      inspectionAt,
      customInspectionFormName,
    )
      .then(() => {
        store.dispatch(setShowAlert(t("PDFDownloadedSuccessfully")));
      })
      .catch((error) => {
        console.error("PDF download failed:", error);
      });
  };

  const handleTouchStart = (event: any) => {
    const { target } = event;
    const selectPaperProps = document.querySelector(".select-options-js");
    if (!selectPaperProps?.contains(target)) {
      setInspectionFormNameStatus(false);
    }
  };

  const initScrollToTop = useCallback(() => {
    if (isInit) {
      scrollToTop();
      setIsInit(false);
    }
  }, [isInit]);

  useEffect(() => {
    if (!!isRequestedMachineInfo && !!groupId && !!machineId) {
      setIsLoading(true);
      getInspectionFormsWeb(groupId, machineId)
        .then((db) => {
          setDbTemplateList(db);
          if (!db.length) {
            initScrollToTop();
            setIsLoading(false);
          }
        })
        .finally(() => {
          setIsRequestInspections(true);
        });
    }
  }, [isRequestedMachineInfo, groupId, machineId]);

  useEffect(() => {
    if (
      !!isRequestInspections &&
      !!groupId &&
      !!machineId &&
      !!inspectionFormName
    ) {
      setIsLoading(true);
      getInspections(groupId, machineId, {
        monthYear: format(
          new Date(dateInspection),
          DATE_TIME_FORMAT_YEAR_AND_MONTH_1,
        ),
        utc: getCurrentUtc(),
        customInspectionFormId: inspectionFormName,
      })
        .then((db) => {
          let result = [
            ...sortCustomInspectionItemsByPosition(db?.customInspectionItems),
            ...[
              {
                name: t("tableMachineDetail.reporter"),
                inspectionItemName: ItemInspectionEnum.STAFF_NAME,
              },
              {
                name: t("machineLocation"),
                inspectionItemName: ItemInspectionEnum.LOCATION,
              },
            ],
          ].map((item: any) => {
            let ob = { ...item } as any;
            Array.from({ length: getDaysInMonth(dateInspection) })?.forEach(
              (_day, i) => {
                ob[
                  format(
                    new Date(
                      getYear(dateInspection),
                      getMonth(dateInspection),
                      i + 1,
                    ),
                    DATE_TIME_FORMAT_1,
                  )
                ] = [];
              },
            );
            return ob;
          });
          db?.customInspections?.forEach((item: any) => {
            result.forEach((element: any) => {
              element?.[
                formatTime(item?.inspectionAt, DATE_TIME_FORMAT_1)
              ]?.push(item);
            });
          });
          setDisplayData(result as any);
        })
        .finally(() => {
          resetIsContinuousLoading();
          setIsLoading(false);
        });
    } else {
      resetIsContinuousLoading();
      setIsLoading(false);
    }
  }, [
    dateInspection,
    groupId,
    inspectionFormName,
    machineId,
    isRequestInspections,
    t,
  ]);

  useEffect(() => {
    if (isMobileDevice && isSafari) {
      document.addEventListener("touchstart", handleTouchStart);
      return () => {
        document.removeEventListener("touchstart", handleTouchStart);
      };
    }
  }, []);

  const [valueStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate());
    return date;
  });

  const handleHeaderCellClick = (inspectionIds: string[]) => {
    setClickedInspectionIds(inspectionIds);
  };

  const handleAddInspectionClick = async () => {
    if (
      !groupId ||
      !machineId ||
      !inspectionFormName ||
      !selectedDate ||
      !selectedTime
    ) {
      alert("Please provide all required inputs.");
      return;
    }

    const inspectionAt = getFormattedDateTime();
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    setIsSubmittingAddInspection(true);
    try {
      const response = await getCustomInspectionFormWeb(
        groupId,
        machineId,
        inspectionFormName,
        { dateTime: inspectionAt, timezone },
      );

      if (!response || !response.inspectionFormId) {
        store.dispatch(
          setShowAlert(t("Anunexpectederroroccurredwhileprocessingthefile")),
        );
        return;
      }

      const { inspectionDetail, latestInspection } =
        parseWebInspectionFormResponse(response as Record<string, unknown>);

      setCreateInspectionPrefill({
        customInspectionFormId: inspectionFormName,
        inspectionAt,
        inspectionDetail,
        latestInspection,
      });
      setIsPopupOpen(false);
      setOpenCreateInspection(true);
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        store.dispatch(
          setShowAlert(
            error.response?.data?.error?.message ||
              error.response?.data?.meta?.errorMessage ||
              t("Anunexpectederroroccurredwhileprocessingthefile"),
          ),
        );
      } else {
        store.dispatch(
          setShowAlert(t("Anunexpectederroroccurredwhileprocessingthefile")),
        );
      }
    } finally {
      setIsSubmittingAddInspection(false);
    }
  };

  const refreshInspectionData = () => {
    if (!groupId || !machineId || !inspectionFormName) return;

    getInspections(groupId, machineId, {
      monthYear: format(
        new Date(dateInspection),
        DATE_TIME_FORMAT_YEAR_AND_MONTH_1,
      ),
      utc: getCurrentUtc(),
      customInspectionFormId: inspectionFormName,
    })
      .then((db) => {
        let result = [
          ...sortCustomInspectionItemsByPosition(db?.customInspectionItems),
          ...[
            {
              name: t("tableMachineDetail.reporter"),
              inspectionItemName: ItemInspectionEnum.STAFF_NAME,
            },
            {
              name: t("machineLocation"),
              inspectionItemName: ItemInspectionEnum.LOCATION,
            },
          ],
        ].map((item: any) => {
          let ob = { ...item } as any;
          Array.from({ length: getDaysInMonth(dateInspection) })?.forEach(
            (_day, i) => {
              ob[
                format(
                  new Date(
                    getYear(dateInspection),
                    getMonth(dateInspection),
                    i + 1,
                  ),
                  DATE_TIME_FORMAT_1,
                )
              ] = [];
            },
          );
          return ob;
        });
        db?.customInspections?.forEach((item: any) => {
          result.forEach((element: any) => {
            element?.[formatTime(item?.inspectionAt, DATE_TIME_FORMAT_1)]?.push(
              item,
            );
          });
        });
        setDisplayData(result as any);
      })
      .finally(() => {
        resetIsContinuousLoading();
      });
  };

  useEffect(() => {
    clearMachineReportsPreferences();
  }, []);

  useEffect(() => {
    if (isMobileDevice && isSafari) {
      document.addEventListener("touchstart", handleTouchStart);
      return () => {
        document.removeEventListener("touchstart", handleTouchStart);
      };
    }
  }, []);

  useEffect(() => {
    if (!inspectionFormName || !dbTemplateList?.length) return;
    const form = dbTemplateList.find(
      (f) => f.customInspectionFormId === inspectionFormName,
    );
    if (form && form.currentStatus !== "PUBLISHED") {
      setIsEditing(false);
      setShowDeleteRow(false);
    }
  }, [dbTemplateList, inspectionFormName]);

  useEffect(() => {
    if (dbTemplateList?.length) {
      const firstNonArchivedItem = dbTemplateList.find(
        (item) => item.currentStatus !== "ARCHIVED",
      );
      setInspectionFormName(
        firstNonArchivedItem?.customInspectionFormId ||
          dbTemplateList[0].customInspectionFormId,
      );
    }
  }, [dbTemplateList]);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await getUser();
        let timeFormat = response?.data?.data?.timeFormat || "yyyy/MM";

        const formatsToModify = ["yyyy/MM/dd", "MM/dd/yyyy", "dd/MM/yyyy"];
        if (formatsToModify.includes(timeFormat)) {
          timeFormat = timeFormat.replace(/^dd\//, "").replace(/\/dd/, "");
        }

        setUserTimeFormat1(timeFormat);
        setUserTimeFormat(response?.data?.data?.timeFormat);
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchUserData();
  }, []);

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const response = await getUserPermissions();
        const userPermissions = response.data?.data;

        const targetGroupId = groupId;

        const matchingGroup = userPermissions.groups.find(
          (group: any) => group.groupId === targetGroupId,
        );

        if (matchingGroup && matchingGroup.permissions) {
          const permissions = matchingGroup.permissions;

          const inspectionResults =
            permissions["INSPECTIONS_AND_MACHINE_REPORTS"];
          const hasPermission =
            inspectionResults === "READ_CREATE" ||
            inspectionResults === "UPDATE_DELETE" ||
            inspectionResults === "READ_CREATE_UPDATE_DELETE";

          setIsButtonVisiblePer(hasPermission);
        } else {
          setIsButtonVisiblePer(false);
        }
      } catch (error) {
        console.error("Failed to fetch permissions:", error);
        setIsButtonVisiblePer(false);
      }
    };

    fetchPermissions();
  }, [groupId]);

  return (
    <>
      <DataDialog
        open={isPopupOpen}
        title={t("addInspections")}
        onClose={(event: any, reason: string) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          handleClosePopup();
        }}
      >
        <DialogContent>
          <div
            style={{
              display: "flex",
              gap: "1rem",
              alignItems: "center",
              marginTop: "0rem",
            }}
          >
            <div>
              <DatePickerInput
                label={t("report.labelStartDate")}
                value={
                  new Date(
                    getYear(dateInspection),
                    getMonth(dateInspection),
                    Math.min(
                      new Date().getDate(),
                      getDaysInMonth(dateInspection),
                    ),
                  )
                }
                onChange={(newValue) => handleChangeStartDate(newValue)}
                onAccept={(newValue) => handleChangeStartDate(newValue)}
                views={["year", "month", "day"]}
                minDate={
                  new Date(getYear(dateInspection), getMonth(dateInspection), 1)
                }
                maxDate={
                  new Date(
                    getYear(dateInspection),
                    getMonth(dateInspection) + 1,
                    0,
                  )
                }
                disableFuture
              />
            </div>

            <div style={{ flex: 1, marginTop: "-8px" }}>
              <TextField
                label={t("time")}
                type="time"
                value={selectedTime}
                onChange={(e) => setSelectedTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                margin="normal"
                style={{ width: "100%" }}
              />
            </div>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              marginTop: "0rem",
            }}
          >
            <strong>{t("")}</strong>
          </div>
        </DialogContent>

        <DialogActions style={{ paddingRight: "0px", paddingBottom: "0px" }}>
          <Button className="btn btn-no-border" onClick={handleClosePopup}>
            {t("dialogReport.cancel")}
          </Button>
          <Button
            className="btn-primary"
            onClick={handleAddInspectionClick}
            disabled={
              !valueStartDate || !selectedTime || isSubmittingAddInspection
            }
          >
            {t("formAction.add")}
          </Button>
        </DialogActions>
      </DataDialog>

      <CreateInspections
        open={openCreateInspection}
        onClose={handleCloseCreateInspection}
        groupId={groupId || ""}
        machineId={machineId || ""}
        prefill={createInspectionPrefill}
        onSuccess={() => {
          refreshInspectionData();
          refreshMachineData();
        }}
      />

      <DataDialog
        open={InspectionPDFdialogOpen}
        setOpen={() => setInspectionPDFdialogOpen(false)}
        disableBackdropClick={true}
        title={
          <p
            style={{
              fontWeight: "bold",
              fontSize: "24px",
              marginBottom: "0px",
              marginTop: "0px",
              color: "black",
            }}
          >
            {t("PDFRemarks")}
          </p>
        }
        className="dialog-container"
      >
        <p className="txt-gray-1 fs-16">{t("pfdComments")}</p>
        <div className="wrapper-text-field mt-8">
          <TextField
            multiline
            rows={3}
            className="input-field"
            label={t("HeaderRemarks")}
            value={pdfHeaderRemarks}
            fullWidth
            inputProps={{ maxLength: 1000 }}
            onChange={handleInputPdfHeaderChange}
            error={pdfHeaderRemarksError}
            helperText={pdfHeaderRemarksError ? t("invalidCharacters") : ""}
            InputProps={{
              endAdornment: (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                  }}
                >
                  {pdfHeaderRemarksError && <IconInputError />}
                </div>
              ),
            }}
          />
          {pdfHeaderRemarksError && (
            <span className="error-text fs-12 ml-15 txt-red"></span>
          )}
        </div>

        <div className="wrapper-text-field mt-8">
          <TextField
            multiline
            rows={3}
            className="input-field"
            label={t("FooterRemarks")}
            value={pdfFooterRemarks}
            fullWidth
            inputProps={{ maxLength: 1000 }}
            onChange={handleInputPdfFooterChange}
            error={pdfFooterRemarksError}
            helperText={pdfFooterRemarksError ? t("invalidCharacters") : ""}
            InputProps={{
              endAdornment: (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                  }}
                >
                  {pdfFooterRemarksError && <IconInputError />}
                </div>
              ),
            }}
          />
          {pdfFooterRemarksError && (
            <span className="error-text fs-12 ml-15 txt-red"></span>
          )}
        </div>

        <div
          className="flex justify-content-end group-btn-footer"
          style={{
            position: "sticky",
            bottom: 0,
            backgroundColor: "white",
            paddingTop: "16px",
            gap: "8px",
            zIndex: 1000,
          }}
        >
          <Button
            className="btn btn-no-border"
            onClick={() => {
              setInspectionPDFdialogOpen(false);

              setPdfHeaderRemarks("");
              setPdfFooterRemarks("");
            }}
          >
            {t("dialogReport.cancel")}
          </Button>

          <Button
            className="btn btn-primary"
            onClick={() => {
              handleOutputMonthlyInspectionPdf();
            }}
            disabled={pdfHeaderRemarksError || pdfFooterRemarksError}
          >
            {t("pdfOutput")}
          </Button>
        </div>
      </DataDialog>

      {dbTemplateList &&
        (!!dbTemplateList?.length ? (
          <div className="inspection-form-tab">
            <div className="wrap-action-inspection flex-centered justify-content-between sp-p24 mb-24 mt-24 gap-16">
              <div className="wrap-date flex-centered justify-content-start">
                <h3 className="m-0 fs-24 txt-black-1 fw-700">
                  {format(dateInspection, userTimeFormat1)}
                </h3>
                <div style={{ display: "flex", gap: "8px" }}>
                  <Button
                    className="btn-no-border-new btn-action-icon"
                    disabled={
                      format(
                        dateInspection,
                        DATE_TIME_FORMAT_YEAR_AND_MONTH,
                      ) === MIN_DATE_CREATE_INSPECTION
                    }
                    onClick={() => handleChangeMonth(-1)}
                  >
                    {isRTL ? (
                      <ArrowForwardIos className="pointer fs-12" />
                    ) : (
                      <ArrowBackIos className="pointer fs-12" />
                    )}
                  </Button>
                  <Button
                    className="btn-no-border-new btn-action-icon"
                    disabled={
                      format(
                        dateInspection,
                        DATE_TIME_FORMAT_YEAR_AND_MONTH,
                      ) === format(new Date(), DATE_TIME_FORMAT_YEAR_AND_MONTH)
                    }
                    onClick={() => handleChangeMonth(1)}
                  >
                    {isRTL ? (
                      <ArrowBackIos className="pointer fs-12" />
                    ) : (
                      <ArrowForwardIos className="pointer fs-12" />
                    )}
                  </Button>
                </div>
                <FormControl
                  className="select-field select-inspection"
                  sx={{ marginInlineStart: "16px" }}
                >
                  <Select
                    open={inspectionFormNameStatus}
                    onOpen={() => setInspectionFormNameStatus(true)}
                    onClose={() => setInspectionFormNameStatus(false)}
                    id="grouped-select"
                    value={inspectionFormName || ""}
                    onChange={handleChange}
                    renderValue={(selected) => {
                      const selectedItem = dbTemplateList.find(
                        (item) => item.customInspectionFormId === selected,
                      );
                      return selectedItem ? selectedItem.name : "";
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 216,
                          width: isMobile() ? window.innerWidth - 48 : 342,
                        },
                        className: `select-options-js ${
                          inspectionFormNameStatus ? "d-block" : "d-none-imp"
                        }`,
                      },
                    }}
                  >
                    {!!dbTemplateList &&
                      dbTemplateList
                        .sort((a, b) => {
                          if (
                            a.currentStatus === "ARCHIVED" &&
                            b.currentStatus !== "ARCHIVED"
                          ) {
                            return 1;
                          }
                          if (
                            a.currentStatus !== "ARCHIVED" &&
                            b.currentStatus === "ARCHIVED"
                          ) {
                            return -1;
                          }
                          return 0;
                        })
                        .map((option: any) => {
                          const isArchived =
                            option.currentStatus === "ARCHIVED";

                          return (
                            <MenuItem
                              key={option?.customInspectionFormId}
                              value={option?.customInspectionFormId}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                padding: "10px 16px",
                              }}
                            >
                              {isArchived && (
                                <div
                                  style={{
                                    marginRight: "10px",
                                    marginTop: "5px",
                                  }}
                                >
                                  <IconDeleteGray />
                                </div>
                              )}
                              <DataTooltip isTextPre width="100%">
                                {option.name}
                              </DataTooltip>
                            </MenuItem>
                          );
                        })}
                  </Select>
                </FormControl>
              </div>

              <div
                className="wrap-btns flex-centered justify-content-start"
                style={{ gap: "16px", flexWrap: "wrap" }}
              >
                {isEditing && isButtonVisiblePer && (
                  <Button className="btn-primary" onClick={handleOpenPopup}>
                    {t("formAction.add")}
                  </Button>
                )}

                {/* {isEditing && (
                  <div style={{ marginRight: "10px" }}>
                    <Button className="btn-primary" onClick={handleSaveForm}>
                      {t("formAction.view")}
                    </Button>
                  </div>
                )} */}

                {!isEditing && (
                  <Button
                    className="btn-primary"
                    onClick={() => {
                      setInspectionPDFdialogOpen(true);
                    }}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <PictureAsPdfOutlined sx={{ fontSize: 18 }} />
                      {t("pdfOutput")}
                    </span>
                  </Button>
                )}

                {/* <div style={{ marginLeft: "10px" }}>
                  {isButtonVisible && isButtonVisiblePer && !isEditing && (
                    <Button
                      className={
                        isEditing ? "btn-white disabled" : "btn-primary"
                      }
                      onClick={() => {
                        if (!isEditing) {
                          handleEditForm();
                        }
                      }}
                      disabled={isEditing}
                    >
                      <span className="flex-centered" style={{ gap: "4px" }}>
                        <IconEdit
                          className="icon-edit"
                          fillColor={isEditing ? "#d3d3d3" : "white"}
                        />
                        {t("formAction.edit")}
                      </span>
                    </Button>
                  )}
                </div> */}

                {isButtonVisible && (
                  <FormControlLabel
                    sx={{
                      gap: "8px",
                      margin: 0,
                    }}
                    control={
                      <Switch
                        className={`btn-switch ${isEditing ? "checked" : ""}`}
                        checked={isEditing}
                        onChange={(e) => {
                          if (e.target.checked) {
                            handleEditForm();
                          } else {
                            handleSaveForm();
                          }
                        }}
                        color="primary"
                      />
                    }
                    label={t("formAction.edit")}
                  />
                )}
              </div>
            </div>
            <TableInspectionFormTab
              key={refreshKey}
              displayData={displayData}
              currentDate={dateInspection}
              didScrollTop={initScrollToTop}
              showDeleteRow={showDeleteRow}
              isEditing={isEditing}
              onHeaderCellClick={handleHeaderCellClick}
              setDisplayData={setDisplayData}
              refreshInspectionData={refreshInspectionData}
              forceTableRefresh={refreshKey}
              isLoading={isLoading}
              onEditInspection={handleEditInspection}
              onDownloadPdf={(
                inspectionId,
                headerRemarks = "",
                footerRemarks = "",
              ) => {
                handleOutputIDPdfDirect(
                  inspectionId,
                  headerRemarks,
                  footerRemarks,
                );
              }}
            />
          </div>
        ) : (
          <div className="empty-table bg-white mt-24">
            <div className="text-center">
              <IconBox />
              <p className="text-content">{t("emptyInspectionData")}</p>
            </div>
          </div>
        ))}
    </>
  );
}
